import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import moment from 'moment';
import LoadingSpinner from '../components/LoadingSpinner';
import { FaSyncAlt, FaExclamationTriangle } from 'react-icons/fa';

const STATUS_STYLES = {
    active: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-600',
    pending_payment: 'bg-blue-100 text-blue-800',
};

// A subscription is "ending soon" once it's within its last 2 deliveries.
const isEndingSoon = (sub) => sub.status === 'active' && (sub.totalCycles - sub.cyclesDelivered) <= 2;

const MySubscriptionsPage = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            const response = await apiClient.get('/subscriptions/my-subscriptions');
            setSubscriptions(response.data);
        } catch (err) {
            console.error('Failed to fetch subscriptions:', err);
            setError('Failed to load your subscriptions.');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (subscription, status) => {
        const confirmMsg = status === 'cancelled'
            ? 'Cancel this subscription? This cannot be undone.'
            : status === 'paused' ? 'Pause future deliveries for this subscription?' : 'Resume this subscription?';
        if (!window.confirm(confirmMsg)) return;

        setUpdatingId(subscription._id);
        try {
            await apiClient.put(`/subscriptions/${subscription._id}/status`, { status });
            fetchSubscriptions();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update subscription.');
        } finally {
            setUpdatingId(null);
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <div className="text-center text-red-500 mt-10">{error}</div>;

    return (
        <div className="bg-gray-100 min-h-screen container mx-auto px-4 py-4 md:py-12">
            <h1 className="text-2xl md:text-4xl font-extrabold mb-4 md:mb-8 text-center text-gray-800 flex items-center justify-center gap-3">
                <FaSyncAlt className="text-green-600" /> My Subscriptions
            </h1>

            {subscriptions.length === 0 ? (
                <div className="text-center text-gray-500 py-8 md:py-12">
                    <p className="text-base md:text-xl">You don't have any active subscriptions yet.</p>
                </div>
            ) : (
                <div className="space-y-6 max-w-3xl mx-auto">
                    {subscriptions.map(sub => (
                        <div key={sub._id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                            {isEndingSoon(sub) && (
                                <div className="mb-4 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3 flex items-center gap-3 text-sm font-semibold">
                                    <FaExclamationTriangle className="text-orange-500 flex-shrink-0" />
                                    Your subscription is ending soon — only {sub.totalCycles - sub.cyclesDelivered} {sub.totalCycles - sub.cyclesDelivered === 1 ? 'delivery' : 'deliveries'} left. Start a new plan to keep getting deliveries without interruption.
                                </div>
                            )}

                            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                                <div className="flex items-center gap-4">
                                    {sub.product?.images?.[0] && (
                                        <img src={sub.product.images[0]} alt={sub.product?.name} className="w-16 h-16 object-cover rounded-lg shadow-sm" />
                                    )}
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-800">{sub.product?.name}</h2>
                                        <p className="text-sm text-gray-500">Pack: {sub.size} · Qty: {sub.qty}</p>
                                        <p className="text-sm text-gray-500">Every {sub.intervalDays} day{sub.intervalDays > 1 ? 's' : ''} · ₹{sub.pricePerCycle} per delivery</p>
                                    </div>
                                </div>
                                <span className={`px-4 py-1 rounded-full text-sm font-semibold capitalize w-fit ${STATUS_STYLES[sub.status] || 'bg-gray-100 text-gray-800'}`}>
                                    {sub.status.replace('_', ' ')}
                                </span>
                            </div>

                            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="font-semibold text-gray-700">Progress</p>
                                    <p className="text-gray-600">{sub.cyclesDelivered} of {sub.totalCycles} deliveries done</p>
                                </div>
                                {sub.status === 'active' && sub.nextOrderDate && (
                                    <div>
                                        <p className="font-semibold text-gray-700">Next Delivery</p>
                                        <p className="text-gray-600">{moment(sub.nextOrderDate).format('MMMM Do YYYY')}</p>
                                    </div>
                                )}
                            </div>

                            {(sub.status === 'active' || sub.status === 'paused') && (
                                <div className="mt-6 flex gap-3">
                                    {sub.status === 'active' ? (
                                        <button
                                            onClick={() => handleStatusChange(sub, 'paused')}
                                            disabled={updatingId === sub._id}
                                            className="px-4 py-2 rounded-md font-semibold text-white bg-yellow-500 hover:bg-yellow-600 transition-colors disabled:opacity-60"
                                        >
                                            Pause
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleStatusChange(sub, 'active')}
                                            disabled={updatingId === sub._id}
                                            className="px-4 py-2 rounded-md font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-60"
                                        >
                                            Resume
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleStatusChange(sub, 'cancelled')}
                                        disabled={updatingId === sub._id}
                                        className="px-4 py-2 rounded-md font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MySubscriptionsPage;
